/**
 * Stage Scorecard Bindings API (Phase B, B-6/B-7)
 *
 * Authoring CRUD for stage_scorecard_bindings: maps a CRM pipeline+stage
 * (exact) or a coarse stage-semantic phase (semantic fallback) to a prompt
 * profile, so stage-aware scoring can pick a scorecard based on where a
 * deal sits in the pipeline. Uses Zod validation from @aica/api-types
 * (SSOT) — same pattern as ScriptsApi.
 *
 * `installationId` on a create or rebind — the CRM install whose stages the
 * tab shows, sent for an exact row (#2139): its ids are the CRM's own, so the
 * server refuses with 409 `crm_installation_changed` when the org's reference
 * install is no longer that one. Left out for a phase row and when the server
 * named none. A delete names the row by id and takes none.
 */

import { BaseApiClient } from '../base';
import { API_ENDPOINTS } from '../endpoints';
import { INSTALLATION_CHANGED_REFUSALS, withInstallation } from '../crm-installation';
import {
  listStageBindingsResponseSchema,
  stageBindingResponseSchema,
  deleteResponseSchema,
  type CreateStageBindingRequest,
  type UpdateStageBindingRequest,
  type ListStageBindingsResponse,
  type StageBindingResponse,
  type DeleteResponse,
} from '@aica/api-types/schemas';

export class StageBindingsApi extends BaseApiClient {
  /** List the org's stage scorecard bindings (exact + semantic rows). */
  async listBindings(): Promise<ListStageBindingsResponse> {
    return this.validatedRequest(
      API_ENDPOINTS.STAGE_BINDINGS,
      listStageBindingsResponseSchema
    ) as Promise<ListStageBindingsResponse>;
  }

  /** Create an exact (pipeline+stage) or semantic (phase) binding. 409 if already taken. */
  async createBinding(
    data: CreateStageBindingRequest,
    installationId?: string
  ): Promise<StageBindingResponse> {
    return this.validatedRequest(
      withInstallation(API_ENDPOINTS.STAGE_BINDINGS, installationId),
      stageBindingResponseSchema,
      {
        method: 'POST',
        body: JSON.stringify(data),
        expectedRefusals: INSTALLATION_CHANGED_REFUSALS,
      }
    ) as Promise<StageBindingResponse>;
  }

  /**
   * Rebind to a different prompt profile. Re-keying which stage/semantic a
   * binding covers is delete + create by design — not exposed here.
   */
  async updateBinding(
    id: string,
    data: UpdateStageBindingRequest,
    installationId?: string
  ): Promise<StageBindingResponse> {
    return this.validatedRequest(
      withInstallation(API_ENDPOINTS.STAGE_BINDINGS_BY_ID(id), installationId),
      stageBindingResponseSchema,
      {
        method: 'PATCH',
        body: JSON.stringify(data),
        expectedRefusals: INSTALLATION_CHANGED_REFUSALS,
      }
    ) as Promise<StageBindingResponse>;
  }

  async deleteBinding(id: string): Promise<DeleteResponse> {
    return this.validatedRequest(
      API_ENDPOINTS.STAGE_BINDINGS_BY_ID(id),
      deleteResponseSchema,
      { method: 'DELETE' }
    ) as Promise<DeleteResponse>;
  }
}
